/* CAUTION: "trees" are actually Iterables with a `children` property that in turn holds a "tree" */

import { filter, flatMap } from 'lodash-es'

/**
 * Pimped version of `_.filter` (https://lodash.com/docs#filter) that can recurse deep.
 *
 * @param {Object} collection The collection to search.
 * @param {Object} source The object of property values to match.
 * @param {String} prop The property of the collection to recurse into.
 * @returns {Array} the new filtered array.
 */
export function whereDeep(collection, source, prop = 'children') {
  return filter(collection, source).concat(
    flatMap(collection, function (item) {
      return whereDeep(item[prop], source, prop)
    }),
  )
}

/**
 * Like Array.prototype.find, but recursing deep.
 *
 * @param {Array} nodes An array of objects with children.
 * @param {Function} predicate Predicate function called on every entry.
 * @param {String} prop Name of the children property. Defaults to "children".
 * @returns {*} The matching node or null.
 */
export function findDeep(nodes, predicate, prop = 'children') {
  let needle = null

  function recursion(children = []) {
    if (needle) return
    children.forEach((node) => {
      if (predicate(node)) return (needle = node)
      recursion(node[prop])
    })
  }

  recursion(nodes)
  return needle
}

export function mapDeep<T>(nodes: T[], predicate: (node: T) => T, prop = 'children'): T[] {
  function recursion(_parent: T, children: T[]) {
    return children.map((node) =>
      predicate({ ...node, ...(node[prop] ? { [prop]: recursion(node, node[prop]) } : {}) }),
    )
  }

  return recursion(undefined as unknown as T, nodes)
}
